// =============================================
// pageEdit — separate pages of a stored document, or combine documents into one
// (Process Docs viewer → "Separate pages" / "Combine documents")
// =============================================
// Both make NEW documents and replace the originals (owner's rule, 2026-10-14): the
// originals are soft-deleted (is_deleted = 1, as everywhere — the rows and their blobs
// stay), but the app offers no way back, and the user is warned of that first.
//
// A new document starts like a fresh upload: status 'Review' (To File), not date-
// verified, Filing fields empty — they are read when it is first opened. It keeps the
// original's account, owner and folder so it stays where the user was working.
// =============================================
import { PDFDocument } from 'pdf-lib';
import { compressBytesToBase64 } from './pdfBytes';
import { storeDocBlob, isServerKey } from './docStorage';
import { sha256Hex } from './documentFingerprint';
import { addRow, saveRow } from '../../db-viewer/data/dataAccess';

/** One PDF per group of pages (0-based page indexes, in the order given). */
export async function cutPdfGroups(bytes: Uint8Array, groups: number[][]): Promise<Uint8Array[]> {
  const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const total = src.getPageCount();
  const out: Uint8Array[] = [];
  for (const group of groups) {
    if (group.length === 0 || group.some(i => i < 0 || i >= total)) throw new Error('A page in the split is not in the document');
    const doc = await PDFDocument.create();
    const pages = await doc.copyPages(src, group);
    pages.forEach(p => doc.addPage(p));
    out.push(await doc.save());
  }
  return out;
}

/** All pages of each PDF, one after the other, as one PDF. */
export async function mergePdfs(list: Uint8Array[]): Promise<Uint8Array> {
  if (list.length === 0) throw new Error('Nothing to combine');
  const doc = await PDFDocument.create();
  for (const bytes of list) {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pages = await doc.copyPages(src, src.getPageIndices());
    pages.forEach(p => doc.addPage(p));
  }
  return doc.save();
}

/** "Bill.pdf" + "_p3-5" → "Bill_p3-5.pdf". */
export function nameWithSuffix(filename: string, suffix: string): string {
  const m = /^(.*?)(\.[A-Za-z0-9]{1,5})?$/.exec(filename || 'document');
  const base = (m && m[1]) || 'document';
  return `${base}${suffix}${(m && m[2]) || '.pdf'}`;
}

/** Page-range label for a group of 0-based pages: "_p3" or "_p3-5" (or "_p1-2+4"). */
export function pagesSuffix(group: number[]): string {
  const nums = group.map(i => i + 1);
  const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
  if (nums.length === 1) return `_p${nums[0]}`;
  return contiguous ? `_p${nums[0]}-${nums[nums.length - 1]}` : `_p${nums.join('+')}`;
}

const NOW = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

/**
 * Store a new PDF as a document that takes its place, account and folder from `like`.
 * Returns the saved row. Throws when the bytes could not be stored.
 */
export async function createDocumentLike(
  like: Record<string, any>,
  bytes: Uint8Array,
  filename: string,
  userId: number | null,
  method: 'split' | 'combine',      // dms_documents.upload_method (migration 149)
  methodAt: string = NOW(),         // one time for every document of the same edit
): Promise<Record<string, any>> {
  const { base64, compressedSize } = await compressBytesToBase64(bytes);
  const blobKey = await storeDocBlob(base64, like.account_owner_id);
  if (!blobKey) throw new Error('the new file could not be stored');
  let fileHash: string | null = null;
  try { fileHash = await sha256Hex(bytes); } catch { /* duplicate check only */ }
  let pageCount: number | null = null;
  try { pageCount = (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(); } catch { /* unknown */ }
  const now = NOW();
  return addRow('dms_documents', {
    account_owner_id: like.account_owner_id,
    user_id: like.user_id ?? userId,
    dms_folders_id: like.dms_folders_id ?? null,
    dms_folders_lvl1_id: like.dms_folders_lvl1_id ?? null,
    dms_folders_lvl2_id: like.dms_folders_lvl2_id ?? null,
    filename,
    original_filename: filename,
    file_hash: fileHash,
    file_base64: isServerKey(blobKey) ? null : base64,
    file_path: blobKey,
    file_size_original: bytes.length,
    file_size_stored: compressedSize,
    mime_type: 'application/pdf',
    page_count: pageCount,
    upload_source: like.upload_source || 'receipts',
    upload_method: method,
    upload_method_at: methodAt,
    upload_date: now,
    status: 'Review',
    owner_type: like.owner_type ?? null,
    owner_id: like.owner_id ?? null,
    is_active: 1,
    is_deleted: 0,
    created_by: userId,
    created_at: now,
    updated_at: now,
  }, 'document_id');
}

/** Soft-delete the documents a split / combine replaced. */
export async function retireDocuments(ids: number[]): Promise<void> {
  const now = NOW();
  for (const id of ids) {
    await saveRow('dms_documents', id, { is_deleted: 1, updated_at: now }, 'document_id');
  }
}
